import { createAction, createSlice } from '@reduxjs/toolkit'
import { Document } from '../../@types/document'
import { Category } from '../../@types/Category'
import { Status } from '../../@types/status'
import { fetchHojasDeCargo } from './actions/getHojasDeCargo'
import { findHojaDeCargo } from './actions/findHojaDeCargo'
import { sendFileForSign } from './actions/sendFileForSign'

interface SupervisorDocumentsListState {
  documents: Document[]
  searchText: string
  page: number
  sort: string
  statusFilter?: Status[]
  createdAtFilter?: {
    from: number
    to: number
  }
  typeFilter?: Category[]
  viewMode: string
  //
  findStatus: string
  showFilterByStatusModal: boolean
  showFilterByDateModal: boolean
  count: string
  showFilterByTypeModal: boolean
  // Pending report
  showReportModal: boolean
  fetchReportStatus: string
  report: any[]
  hojaDecargoDetail: any[]
  findHojaDeCargoStatus: string
  datosHojaDeCargo: any[]
  signFileStatus: string
  qrModalStatus: boolean
}

const today = new Date() // Obtener la fecha actual
const firstDayOfYear = new Date(today.getFullYear(), 0, 1) // Crear una nueva fecha con el año actual y el mes 0 (enero) y el día 1

const initialState = {
  documents: [],
  searchText: '',
  page: 1,
  sort: 'createdAt DESC',
  statusFilter: [{ id: 1, name: 'Pendiente', checked: true }],
  typeFilter: [],
  createdAtFilter: {
    from: firstDayOfYear.getTime(),
    to: today.getTime()
  },
  viewMode: 'grid',
  //
  findStatus: '',
  showFilterByStatusModal: false,
  showFilterByDateModal: false,
  count: '0',
  showFilterByTypeModal: false,
  showReportModal: false,
  fetchReportStatus: '',
  report: [],
  hojaDecargoDetail: [],
  findHojaDeCargoStatus: '',
  datosHojaDeCargo: [],
  signFileStatus: '',
  qrModalStatus: false
} as SupervisorDocumentsListState

// Mutaciones sincrónicas de datos
export const setSearchText = createAction<string>('SET_SEARCH_TEXT')
export const incrementPage = createAction('INCREMENT_PAGE')
export const resetDocumentList = createAction('RESET_DOCUMENT_LIST')
export const setSort = createAction<string>('SET_SORT')
export const setStatusFilter = createAction<any>('SET_STATUS_FILTER')
export const setTypeFilter = createAction<any>('SET_TYPE_FILTER')
export const setCreatedAtFilter = createAction<any>('SET_CREATED_AT_FILTER')
export const setViewMode = createAction<string>('SET_VIEW_MODE')
export const setFilterByStatusModal = createAction<boolean>('SET_FILTER_BY_STATUS_MODAL')
export const setFilterByDateModal = createAction<boolean>('SET_FILTER_BY_DATE_MODAL_SUPERVISOR')
export const setFilterByTypeModal = createAction<boolean>('SET_FILTER_BY_TYPE_MODAL_SUPERVISOR')
export const setShowReportModal = createAction<boolean>('SET_SHOW_REPORT_MODAL_SUPERVISOR')
export const setModalQrStatus = createAction<boolean>('SET_QR_MODAL_STATUS')

const supervisorDocumentsListSlice = createSlice({
  name: 'supervisorDocumentsList',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(resetDocumentList, (state) => {
        state.documents = []
        state.page = 1
        state.datosHojaDeCargo = []
      })
      .addCase(setViewMode, (state, { payload }) => {
        state.viewMode = payload
      })
      .addCase(incrementPage, (state) => {
        state.page += 1
      })
      // Buscar documentos
      .addCase(fetchHojasDeCargo.pending, (state) => {
        state.findStatus = 'pending'
      })

      .addCase(fetchHojasDeCargo.fulfilled, (state, { payload }) => {
        if (payload.length > 1) {
          state.count = payload.length
        }
        // Validar que `payload` sea un array
        if (Array.isArray(payload)) {
          state.documents.push(...payload)
        } else {
          console.error('El payload no es un array o no es válido:', payload)
        }

        state.findStatus = 'fulfilled'
      })
      .addCase(fetchHojasDeCargo.rejected, (state) => {
        state.findStatus = 'rejected'
      })
      .addCase(setSearchText, (state, { payload = '' }) => {
        state.searchText = payload
        state.page = 1
        state.documents = []
      })
      .addCase(setSort, (state, { payload }) => {
        state.sort = payload
        state.page = 1
        state.documents = []
      })
      .addCase(setStatusFilter, (state, { payload }) => {
        state.statusFilter = payload
        state.page = 1
        state.documents = []
      })
      .addCase(setCreatedAtFilter, (state, { payload }) => {
        state.createdAtFilter = payload
        state.page = 1
        state.documents = []
      })
      .addCase(setTypeFilter, (state, { payload }) => {
        state.typeFilter = payload
        state.page = 1
        state.documents = []
      })
      .addCase(setFilterByDateModal, (state, { payload }) => {
        state.showFilterByDateModal = payload
      })
      .addCase(setFilterByStatusModal, (state, { payload }) => {
        state.showFilterByStatusModal = payload
      })
      .addCase(setFilterByTypeModal, (state, { payload }) => {
        state.showFilterByTypeModal = payload
      })
      .addCase(setShowReportModal, (state, { payload }) => {
        state.showReportModal = payload
      })
      .addCase(findHojaDeCargo.pending, (state) => {
        state.findHojaDeCargoStatus = 'pending'
        state.datosHojaDeCargo = []
      })
      .addCase(findHojaDeCargo.fulfilled, (state, { payload }) => {
        state.findHojaDeCargoStatus = 'fulfilled'
        state.datosHojaDeCargo = payload
      })
      .addCase(findHojaDeCargo.rejected, (state, { payload }) => {
        state.findHojaDeCargoStatus = 'rejected'
      })
      .addCase(sendFileForSign.fulfilled, (state, { payload }) => {
        state.signFileStatus = 'fulfilled'
      })
      .addCase(sendFileForSign.pending, (state, { payload }) => {
        state.signFileStatus = 'pending'
      })
      .addCase(setModalQrStatus, (state, { payload }) => {
        state.qrModalStatus = payload
      })
  }
})

export const supervisorDocumentsList = supervisorDocumentsListSlice.reducer
